import React, { useContext, useState } from 'react';
import { Spinner, Tabs } from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { ContentLayout } from 'src/components/ContentLayout';
import { DateRangePicker } from 'src/components/DateRangePicker';
import { ViolationsTable } from 'src/components/ViolationsTable';
import { processMetricsData } from 'src/utils/dataQuailtyUtils';
import { apiGetRequest } from 'src/utils/mlPigeonAPIRequest';
import {
  convertDateToEpochTimeInSeconds,
  getDateRangeFromToday,
} from 'src/utils/timeUtils';
import { FeatureMetrics } from 'src/components/FeatureMetrics';
import { ErrorsByJobTable } from 'src/components/ErrorsByJobTable';
import { Link } from 'src/components/Link';
import { AccountContext } from 'src/context/accountContext';
import { Alert, Box } from '@cloudscape-design/components';

export const DataQualityMetricsPage = () => {
  const { selectedAccount } = useContext(AccountContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    applicationId,
    modelVersionId,
    modelArtifactId,
    trainingJobId,
    monitoringScheduleId,
  } = useParams();
  const params = new URLSearchParams(searchParams);
  const startDate = params.get('startDate');
  const endDate = params.get('endDate');
  const initDateRange =
    startDate && endDate
      ? { startDate: new Date(startDate), endDate: new Date(endDate) }
      : getDateRangeFromToday(7);
  const [timeFilter, setTimeFilter] = useState(initDateRange);
  const [selectedNumericalFeatures, setSelectedNumericalFeatures] = useState<
    string[]
  >([]);
  const [selectedCategoricalFeatures, setSelectedCategoricalFeatures] =
    useState<string[]>([]);
  const [numericalBaselineJob, setNumericalBaselineJob] =
    useState<DataQualityNumericalMetricsDataJob | null>(null);
  const [categoricalBaselineJob, setCategoricalBaselineJob] =
    useState<DataQualityCategoricalMetricsDataJob | null>(null);
  const [numericalData, setNumericalData] = useState<{
    features: Set<string>;
    jobs: DataQualityNumericalMetricsDataJob[];
  } | null>(null);
  const [categoricalData, setCategoricalData] = useState<{
    features: Set<string>;
    jobs: DataQualityCategoricalMetricsDataJob[];
  } | null>(null);

  const modelMetricsQuery = useQuery<ModelMetric[], Error>({
    queryKey: [
      'model-metrics',
      modelArtifactId,
      trainingJobId,
      monitoringScheduleId,
      timeFilter,
    ],
    queryFn: () => {
      const startOfDay = timeFilter.startDate;
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = timeFilter.endDate;
      endOfDay.setHours(23, 59, 59, 999);
      return apiGetRequest({
        endpoint: 'modelMetric',
        params: {
          modelArtifactId: trainingJobId ? '' : modelArtifactId,
          trainingJobId: trainingJobId ?? monitoringScheduleId ?? '',
          metricsType: 'DATAQUALITY',
          startTime: convertDateToEpochTimeInSeconds(startOfDay).toString(),
          endTime: convertDateToEpochTimeInSeconds(endOfDay).toString(),
        },
      }).then((data) => {
        const { numericalData, categoricalData } = processMetricsData(data);
        setNumericalData(numericalData);
        setCategoricalData(categoricalData);
        setSelectedNumericalFeatures(
          Array.from(numericalData.features).slice(0, 5),
        );
        if (trainingJobId) {
          setNumericalBaselineJob(
            numericalData?.jobs?.find((job) => job.jobName === trainingJobId) ||
              null,
          );
          setCategoricalBaselineJob(
            categoricalData?.jobs?.find(
              (job) => job.jobName === trainingJobId,
            ) || null,
          );
        }
        setSelectedCategoricalFeatures(
          Array.from(categoricalData.features).slice(0, 5),
        );
        return data;
      });
    },
  });

  return (
    <ContentLayout
      heading={
        <>
          <h1>Data Quality Metrics</h1>
        </>
      }
    >
      <DateRangePicker
        initDateRange={initDateRange}
        changeDateRange={setTimeFilter}
        setSearchParams={setSearchParams}
        label="Job Run"
      />

      <p>
        {!modelMetricsQuery.isLoading ? (
          `${modelMetricsQuery.data?.length || '0'} job(s) found.`
        ) : (
          <Spinner />
        )}
      </p>

      {(trainingJobId || monitoringScheduleId) && (
        <Box margin={{ bottom: 'xl' }}>
          <Alert
            action={
              <Link
                to={`/accounts/${selectedAccount?.id}/applications/${applicationId}/modelVersions/${modelVersionId}/modelArtifacts/${modelArtifactId}/dataQualityMetrics`}
              >
                Clear Job Filter
              </Link>
            }
            header="Job Filter"
          >
            {`The current view is restricting the jobs in the time range to jobs that are related to: ${
              trainingJobId
                ? `${trainingJobId} - TRAINING JOB`
                : `${monitoringScheduleId} - MONITORING SCHEDULE`
            }`}
          </Alert>
        </Box>
      )}

      {modelMetricsQuery.error && (
        <Box margin={{ bottom: 'l' }}>
          <Alert type="error" header="Unable to fetch data">
            {`${
              modelMetricsQuery?.error?.message || ''
            } If the data fetch has failed, it's possible that the amount of data
          returned exceeds the Lambda payload limit. In that case, you can try
          to reduce the time range, using the controls above. `}
          </Alert>
        </Box>
      )}

      <ErrorsByJobTable
        errorMessages={modelMetricsQuery.data?.reduce<
          { jobId: string; jobType: string; errorMessages: string[] }[]
        >((prev, curr) => {
          if (curr.errorMessages) {
            prev.push({
              jobId: curr.jobId,
              jobType: curr.jobType,
              errorMessages: curr.errorMessages || [],
            });
          }
          return prev;
        }, [])}
      />

      <div className="mb-20">
        <ViolationsTable
          loading={modelMetricsQuery.isLoading}
          violationData={modelMetricsQuery.data?.reduce<JobMetricViolation[]>(
            (prev, curr) => {
              if (curr.constraintViolations) {
                const violations: MetricViolation[] = JSON.parse(
                  curr.constraintViolations,
                ).violations;
                violations?.forEach((violation) => {
                  prev.push({
                    jobId: curr.jobId,
                    jobType: curr.jobType,
                    createdDate: curr.createdDate,
                    feature_name: violation.feature_name,
                    constraint_check_type: violation.constraint_check_type,
                    description: violation.description,
                  });
                });
              }

              return prev;
            },
            [],
          )}
        />
      </div>

      <h2>Feature Metrics</h2>

      <Tabs
        tabs={[
          {
            label: 'Numerical Statistics',
            id: 'numerical-tab',
            content: (
              <FeatureMetrics
                loading={modelMetricsQuery.isLoading}
                mode="numerical"
                // @ts-expect-error
                baselineJob={numericalBaselineJob}
                setBaselineJob={setNumericalBaselineJob}
                data={numericalData}
                selectedFeatures={selectedNumericalFeatures}
                setSelectedFeatures={setSelectedNumericalFeatures}
              />
            ),
          },
          {
            label: 'Categorical Statistics',
            id: 'categorical-tab',
            content: (
              <FeatureMetrics
                loading={modelMetricsQuery.isLoading}
                mode="categorical"
                // @ts-expect-error
                baselineJob={categoricalBaselineJob}
                // @ts-expect-error
                setBaselineJob={setCategoricalBaselineJob}
                data={categoricalData}
                selectedFeatures={selectedCategoricalFeatures}
                setSelectedFeatures={setSelectedCategoricalFeatures}
              />
            ),
          },
        ]}
      />
    </ContentLayout>
  );
};
